import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const serviceOptions = [
  'コンサルティング',
  '広告・プロモーション',
  '店舗・空間デザイン',
  '人材育成',
  'その他',
];

type FormState = {
  company: string;
  name: string;
  email: string;
  phone: string;
  services: string[];
  message: string;
  privacy: boolean;
};

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({
    company: '',
    name: '',
    email: '',
    phone: '',
    services: [],
    message: '',
    privacy: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const toggleService = (s: string) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.includes(s)
        ? prev.services.filter((x) => x !== s)
        : [...prev.services, s],
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (!form.privacy) {
      setError('プライバシーポリシーへの同意が必要です。');
      return;
    }
    if (!form.name || !form.email || !form.message) {
      setError('必須項目を入力してください。');
      return;
    }
    if (form.message.length > 500) {
      setError('お問い合わせ内容は500文字以内でご入力ください。');
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirmedSend = async () => {
    setShowConfirm(false);
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('company', form.company);
      body.append('name', form.name);
      body.append('email', form.email);
      body.append('phone', form.phone);
      body.append('services', form.services.join('、'));
      body.append('message', form.message);
      body.append('privacy', form.privacy ? '同意済み' : '未同意');

      const res = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const text = await res.text();
      if (res.ok && text.trim() === 'OK') {
        setSubmitted(true);
      } else {
        setError('送信に失敗しました。しばらくしてから再度お試しください。');
      }
    } catch {
      setError('送信に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Split layout */}
      <div className="flex flex-col lg:flex-row min-h-screen pt-20">
        {/* Left info */}
        <div className="relative w-full lg:w-1/2 bg-[#0A0E1A] flex flex-col justify-center px-8 md:px-14 py-16 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20minimalist%20Japanese%20business%20consultation%20meeting%20dark%20moody%20professional%20office%20warm%20lights%20bokeh%20night&width=700&height=900&seq=contact-bg-1&orientation=portrait"
            alt="お問い合わせ"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-20"
          />
          <div className="relative z-10">
            <p className="text-xs tracking-[0.3em] text-[#C8501A] uppercase mb-4">Contact Us</p>
            <h1 className="text-3xl md:text-4xl font-black text-white leading-tight mb-6">
              まずは、<br />ご相談から。
            </h1>
            <p className="text-sm text-white/60 leading-relaxed mb-10 max-w-sm">
              お気軽にご連絡ください。通常1〜2営業日以内にご返信いたします。
            </p>

            <div className="space-y-5">
              <a
                href="tel:050-8896-2530"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 group-hover:border-[#C8501A] transition-colors">
                  <i className="ri-phone-line text-white/60 group-hover:text-[#C8501A] transition-colors" />
                </div>
                <div>
                  <p className="text-xs text-white/40 tracking-wider">Phone</p>
                  <p className="text-sm text-white font-medium">050-8896-2530</p>
                </div>
              </a>
              <a
                href="mailto:banking@ala-info.com"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 group-hover:border-[#C8501A] transition-colors">
                  <i className="ri-mail-line text-white/60 group-hover:text-[#C8501A] transition-colors" />
                </div>
                <div>
                  <p className="text-xs text-white/40 tracking-wider">Email</p>
                  <p className="text-sm text-white font-medium">banking@ala-info.com</p>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20">
                  <i className="ri-map-pin-line text-white/60" />
                </div>
                <div>
                  <p className="text-xs text-white/40 tracking-wider">Address</p>
                  <p className="text-sm text-white font-medium">東京都豊島区南長崎２丁目１２番１０号<br />レーベンシュロス２０２号</p>
                </div>
              </div>

              <div className="mt-6 rounded-2xl overflow-hidden border border-white/10">
                <iframe
                  src="https://www.google.com/maps?q=%E6%9D%B1%E4%BA%AC%E9%83%BD%E8%B1%8A%E5%B3%B6%E5%8C%BA%E5%8D%97%E9%95%B7%E5%B4%8E%EF%BC%92%E4%B8%81%E7%9B%AE%EF%BC%91%EF%BC%92%E7%95%AA%EF%BC%91%EF%BC%90%E5%8F%B7%E3%83%AC%E3%83%BC%E3%83%99%E3%83%B3%E3%82%B7%E3%83%A5%E3%83%AD%E3%82%B9%EF%BC%92%EF%BC%90%EF%BC%92%E5%8F%B7&output=embed"
                  width="100%"
                  height="220"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="株式会社ＡＬＡ 所在地"
                ></iframe>
              </div>
            </div>
          </div>
        </div>

        {/* Right form */}
        <div className="w-full lg:w-1/2 bg-white flex flex-col justify-center px-8 md:px-14 py-14">
          <form
            id="contact-form"
            onSubmit={handleSubmit}
            className="max-w-md w-full mx-auto space-y-5"
          >
              <div>
                <p className="text-xs tracking-[0.3em] text-[#C8501A] uppercase mb-2">お問い合わせフォーム</p>
                <h2 className="text-2xl font-black text-[#0A0E1A]">Contact Form</h2>
              </div>

              {/* Company */}
              <div>
                <label className="block text-xs font-medium text-[#0A0E1A] mb-1.5">
                  会社名・屋号
                </label>
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="株式会社〇〇"
                  className="w-full px-4 py-3 rounded-lg bg-[#F5F2ED] border border-transparent focus:border-[#C8501A] focus:outline-none text-sm text-[#0A0E1A] placeholder:text-[#C8C4BC]"
                />
              </div>

              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-[#0A0E1A] mb-1.5">
                  お名前 <span className="text-[#C8501A]">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="山田 太郎"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-[#F5F2ED] border border-transparent focus:border-[#C8501A] focus:outline-none text-sm text-[#0A0E1A] placeholder:text-[#C8C4BC]"
                />
              </div>

              {/* Email & Phone row */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-[#0A0E1A] mb-1.5">
                    メールアドレス <span className="text-[#C8501A]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="example@email.com"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-[#F5F2ED] border border-transparent focus:border-[#C8501A] focus:outline-none text-sm text-[#0A0E1A] placeholder:text-[#C8C4BC]"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-[#0A0E1A] mb-1.5">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="090-0000-0000"
                    className="w-full px-4 py-3 rounded-lg bg-[#F5F2ED] border border-transparent focus:border-[#C8501A] focus:outline-none text-sm text-[#0A0E1A] placeholder:text-[#C8C4BC]"
                  />
                </div>
              </div>

              {/* Services checkboxes */}
              <div>
                <label className="block text-xs font-medium text-[#0A0E1A] mb-2">
                  ご興味のあるサービス（複数選択可）
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {serviceOptions.map((s) => (
                    <label
                      key={s}
                      className="flex items-center gap-2 cursor-pointer select-none"
                    >
                      <div
                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                          form.services.includes(s)
                            ? 'bg-[#C8501A] border-[#C8501A]'
                            : 'bg-white border-[#C8C4BC]'
                        }`}
                        onClick={() => toggleService(s)}
                      >
                        {form.services.includes(s) && (
                          <i className="ri-check-line text-white text-xs" />
                        )}
                      </div>
                      <span className="text-xs text-[#6B6560]">{s}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-medium text-[#0A0E1A] mb-1.5">
                  お問い合わせ内容 <span className="text-[#C8501A]">*</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) setForm({ ...form, message: e.target.value });
                  }}
                  rows={4}
                  placeholder="ご相談内容をご記入ください（500文字以内）"
                  required
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg bg-[#F5F2ED] border border-transparent focus:border-[#C8501A] focus:outline-none text-sm text-[#0A0E1A] placeholder:text-[#C8C4BC] resize-none"
                />
                <p className="text-right text-xs text-[#9C9490] mt-1">{form.message.length}/500</p>
              </div>

              {/* Privacy */}
              <div>
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                      form.privacy
                        ? 'bg-[#C8501A] border-[#C8501A]'
                        : 'bg-white border-[#C8C4BC]'
                    }`}
                    onClick={() => setForm({ ...form, privacy: !form.privacy })}
                  >
                    {form.privacy && (
                      <i className="ri-check-line text-white text-xs" />
                    )}
                  </div>
                  <span className="text-xs text-[#6B6560] leading-relaxed">
                    <Link to="/privacy" className="text-[#C8501A] underline hover:no-underline" target="_blank">
                      プライバシーポリシー
                    </Link>
                    に同意する
                    <span className="text-[#C8501A]"> *</span>
                  </span>
                </label>
              </div>

              {/* Error */}
              {error && (
                <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 bg-[#0A0E1A] text-white text-sm font-medium rounded-full hover:bg-[#C8501A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> 送信中...
                  </>
                ) : (
                  <>
                    送信する <i className="ri-arrow-right-line" />
                  </>
                )}
              </button>
          </form>
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-[100] bg-black/55 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-2xl p-8 shadow-2xl">
            <div className="mb-6">
              <p className="text-xs tracking-[0.3em] text-[#C8501A] uppercase mb-2">Confirm</p>
              <h3 className="text-2xl font-black text-[#0A0E1A] mb-2">入力内容の確認</h3>
              <p className="text-sm text-[#6B6560]">送信前に内容をご確認ください。</p>
            </div>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {[
                ['会社名・屋号', form.company || '未入力'],
                ['お名前', form.name],
                ['メールアドレス', form.email],
                ['電話番号', form.phone || '未入力'],
                ['ご興味のあるサービス', form.services.length ? form.services.join('、') : '未選択'],
                ['お問い合わせ内容', form.message],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl bg-[#F5F2ED] px-4 py-3">
                  <p className="text-[11px] tracking-[0.2em] text-[#9C9490] uppercase mb-1">{label}</p>
                  <p className="text-sm text-[#0A0E1A] whitespace-pre-wrap break-words">{value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col-reverse sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="px-5 py-3 border border-[#DDD9D3] rounded-full text-sm font-medium text-[#6B6560] hover:bg-[#F5F2ED] transition-colors cursor-pointer whitespace-nowrap"
              >
                戻る
              </button>
              <button
                type="button"
                onClick={handleConfirmedSend}
                className="px-5 py-3 bg-[#0A0E1A] text-white text-sm font-medium rounded-full hover:bg-[#C8501A] transition-colors cursor-pointer whitespace-nowrap"
              >
                この内容で送信
              </button>
            </div>
          </div>
        </div>
      )}

      {submitting && (
        <div className="fixed inset-0 z-[110] bg-black/55 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl p-8 text-center shadow-2xl">
            <div className="w-14 h-14 mx-auto mb-5 border-4 border-[#EAE5DE] border-t-[#C8501A] rounded-full animate-spin"></div>
            <p className="text-xs tracking-[0.3em] text-[#C8501A] uppercase mb-2">Sending</p>
            <h3 className="text-2xl font-black text-[#0A0E1A] mb-2">送信中です</h3>
            <p className="text-sm text-[#6B6560]">内容を送信しています。しばらくお待ちください。</p>
          </div>
        </div>
      )}

      {error && !submitting && !showConfirm && (
        <div className="fixed inset-0 z-[120] bg-black/55 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl p-8 text-center shadow-2xl">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-5">
              <i className="ri-error-warning-fill text-2xl text-red-500" />
            </div>
            <p className="text-xs tracking-[0.3em] text-red-500 uppercase mb-2">Error</p>
            <h3 className="text-2xl font-black text-[#0A0E1A] mb-2">送信に失敗しました</h3>
            <p className="text-sm text-[#6B6560] mb-6">{error}</p>
            <button
              type="button"
              onClick={() => setError('')}
              className="px-5 py-3 border border-[#DDD9D3] rounded-full text-sm font-medium text-[#6B6560] hover:bg-[#F5F2ED] transition-colors cursor-pointer whitespace-nowrap"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {submitted && (
        <div className="fixed inset-0 z-[120] bg-black/55 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl p-8 text-center shadow-2xl">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#C8501A]/10 mx-auto mb-5">
              <i className="ri-check-line text-2xl text-[#C8501A]" />
            </div>
            <p className="text-xs tracking-[0.3em] text-[#C8501A] uppercase mb-2">Completed</p>
            <h3 className="text-2xl font-black text-[#0A0E1A] mb-2">送信完了しました</h3>
            <p className="text-sm text-[#6B6560] leading-relaxed mb-6">
              お問い合わせありがとうございます。<br />
              1〜2営業日以内にご返信いたします。
            </p>
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="px-5 py-3 bg-[#0A0E1A] text-white text-sm font-medium rounded-full hover:bg-[#C8501A] transition-colors cursor-pointer whitespace-nowrap"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
